import { T } from 'consts/translationKeys'
import { IEffectSliderProps } from '../effect-slider/types'
import EffectSlider from '../effect-slider'
import { useActiveItems } from 'containers/Timeline/Header/ActionButtons/hooks/use-active-items'
import { IAudioItem, TAudioType } from 'types/audio'
import { IMediaItem, TMediaItemType } from 'types/media'
import { IAudioEffect, TAudioFadeEffectType } from 'types/common'
import { isVisualMediaItem } from 'utils/media'
import { AUDIO_EFFECT_STEP_COEFFICIENT, getMaxFadeDuration } from 'utils/audio'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { dispatch } from 'store'
import debounce from 'lodash.debounce'
import { projectDataThunks } from 'store/features/projectData/thunks'

interface IProps {
  label: T
  type: TAudioFadeEffectType
}

const createFadeEffect = (type: TAudioFadeEffectType): IAudioEffect => ({
  id: crypto.randomUUID(),
  key: 'fade',
  type,
  order: type === 'fade-in' ? 0 : 1,
  meta: {
    duration: 0
  }
})

export const FadeSlider = ({ label, type }: IProps) => {
  const { activeMediaItems, activeAudioItems } = useActiveItems()
  const allActiveItems = [...activeAudioItems, ...activeMediaItems]
  const activeItem: IAudioItem | IMediaItem = allActiveItems[0]
  const effects: IAudioEffect[] =
    (isVisualMediaItem(activeItem.type)
      ? (activeItem as IMediaItem).meta.audioEffects
      : (activeItem as IAudioItem).audioEffects) ?? []

  const existingEffect = effects.find(effect => effect.type === type)

  // Keep a stable effect (and id) for this item+type. While dragging, the
  // dispatch is debounced, so `existingEffect` stays undefined for several
  // renders — without memoizing we'd mint a fresh id on every render and the
  // reducer would push a new effect each time instead of updating one.
  const targetEffect: IAudioEffect = useMemo(
    () => existingEffect ?? createFadeEffect(type),
    [existingEffect, type]
  )

  // A single debounced dispatcher. Recreating the debounced fn on every change
  // (as `debounce(...)()`) defeats debouncing — each call gets its own timer.
  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (
          id: string,
          itemType: TAudioType | TMediaItemType,
          effect: IAudioEffect
        ) =>
          dispatch(projectDataThunks.updateAudioEffect(id, itemType, effect)),
        400
      ),
    []
  )

  const [value, setValue] = useState(targetEffect.meta.duration)

  // Re-sync the slider when the selection changes — a different item carries a
  // different stored duration. `targetEffect` is memoized, so this only fires on
  // a real change (selection switch, or the debounced write landing in store).
  useEffect(() => {
    setValue(targetEffect.meta.duration)
  }, [targetEffect])

  const onValueChange = useCallback(
    (value: number) => {
      const fixedValue = +value.toFixed(AUDIO_EFFECT_STEP_COEFFICIENT)
      setValue(fixedValue)
      debouncedUpdate(activeItem.id, activeItem.type, {
        ...targetEffect,
        meta: {
          ...targetEffect.meta,
          duration: fixedValue
        }
      })
    },
    [activeItem, targetEffect, debouncedUpdate]
  )

  const fadeInConfig: IEffectSliderProps = {
    value,
    onValueChange,
    label,
    duration: getMaxFadeDuration(activeItem.duration)
  }

  // if there are more than one items selected at the same time, we don't provide fade slider
  return allActiveItems.length === 1 ? <EffectSlider {...fadeInConfig} /> : null
}
